import { useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, Typography, Button, Tooltip } from "antd";
import {
  FolderOutlined,
  ShareAltOutlined,
  DeleteOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useMe } from "@/hooks/useMe";

const { Sider } = Layout;
const { Text } = Typography;

type MenuItem = Required<MenuProps>["items"][number];

const baseMenuItems: MenuItem[] = [
  {
    key: "/share",
    icon: <FolderOutlined />,
    label: "All files",
  },
  {
    key: "/share/shared-with-me",
    icon: <ShareAltOutlined />,
    label: "Shared with me",
  },
  {
    key: "/share/shredder",
    icon: <DeleteOutlined />,
    label: "Shredder",
  },
];

interface SidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
}

export default function Sidebar({ collapsed = false, onCollapse }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSupervisor, isSuperadmin } = useMe();

  // Append role-gated entries. Backend still enforces access; this only hides
  // navigation the user can't use.
  const menuItems: MenuItem[] = [
    ...baseMenuItems,
    ...(isSupervisor || isSuperadmin
      ? [
          {
            key: "/share/supervision",
            icon: <EyeOutlined />,
            label: "Supervision",
          },
        ]
      : []),
  ];

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    navigate(key);
  };

  const getSelectedKey = () => {
    const pathname = location.pathname;
    // Check for special pages first
    if (pathname === "/share/shared-with-me") {
      return "/share/shared-with-me";
    }
    if (pathname === "/share/shredder") {
      return "/share/shredder";
    }
    if (pathname === "/share/supervision") {
      return "/share/supervision";
    }
    // Default to "All files" for root and folder pages
    if (pathname === "/share" || pathname.startsWith("/share/")) {
      return "/share";
    }
    return pathname;
  };

  return (
    <Sider
      width={260}
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
      style={{
        background: "linear-gradient(180deg, #ffffff 0%, #FAFBFC 100%)",
        borderRight: "1px solid rgba(0, 0, 0, 0.04)",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
        boxShadow: "1px 0 12px rgba(0, 0, 0, 0.02)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: collapsed ? "20px 12px" : "24px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img
              src="/secondary-logo-gradient.png"
              alt="AIXA"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
          {!collapsed && (
            <div>
              <Text strong style={{ fontSize: 18, color: "#111827", display: "block", lineHeight: 1.2, letterSpacing: "0.5px" }}>
                AIXA <span style={{ fontWeight: 400 }}>Share</span>
              </Text>
              <Text style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.3px" }}>
                Secure Sharing
              </Text>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <div style={{ flex: 1, overflow: "auto", paddingTop: 16, paddingBottom: 16 }}>
          <Menu
            mode="inline"
            selectedKeys={[getSelectedKey()]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              border: "none",
              background: "transparent",
            }}
          />
        </div>

        {/* Collapse Button */}
        <div
          style={{
            padding: collapsed ? "16px 12px" : "16px 24px",
            borderTop: "1px solid rgba(0, 0, 0, 0.06)",
            display: "flex",
            justifyContent: collapsed ? "center" : "flex-end",
          }}
        >
          <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
            <Button
              type="text"
              icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
              onClick={() => onCollapse?.(!collapsed)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6B7280",
                background: "rgba(0, 0, 0, 0.02)",
                border: "1px solid rgba(0, 0, 0, 0.06)",
              }}
            />
          </Tooltip>
        </div>
      </div>
    </Sider>
  );
}
